// src/pages/Login.jsx
import React, { useReducer, Fragment, useContext, useState, useEffect, useMemo } from "react";
import { withCookies } from "react-cookie";
import { Link, useHistory, useLocation } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTranslation } from "react-i18next";
import { SnackbarContext } from "../context/SnackbarContext";
import { useAPI } from "../context/ApiContext";
import {
  START_FETCH, FETCH_SUCCESS, ERROR_CATCHED, INPUT_EDIT_LOG,
} from "../components/actionTypes";

const initialState = { isLoading: false, error: "", credentialsLog: { email: "", password: "" } };
const loginReducer = (s, a) => {
  switch (a.type) {
    case START_FETCH: return { ...s, isLoading: true, error: "" };
    case FETCH_SUCCESS: return { ...s, isLoading: false };
    case ERROR_CATCHED: return { ...s, error: a.payload || "Email or password is not correct!", isLoading: false };
    case INPUT_EDIT_LOG: return { ...s, credentialsLog: { ...s.credentialsLog, [a.inputName]: a.payload }, error: "" };
    default: return s;
  }
};

const Login = ({ topOffset = 0 }) => {
  const history = useHistory();
  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const nextPath = qs.get("next");
  const { t } = useTranslation();
  const { newSnack } = useContext(SnackbarContext);
  const [state, dispatch] = useReducer(loginReducer, initialState);
  const [showPw, setShowPw] = useState(false);

  const { isAuthenticated, loginWithEmail } = useAPI();
  const SOCIAL_REDIRECT_URI = process.env.REACT_APP_SOCIAL_REDIRECT_URI || `${window.location.origin}/auth/callback`;
  const LINE_CHANNEL_ID = process.env.REACT_APP_LINE_CHANNEL_ID || "";

  useEffect(() => { if (isAuthenticated) history.replace("/home"); }, [isAuthenticated, history]);

  // --- Appプロモ制御（Login専用キーで記憶） ---
const APP_STORE_URL = "https://apps.apple.com/jp/app/nicelinks/id6754094457";
const DISMISS_KEY_LOGIN = "nl_app_promo_login_dismissed_v1";

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent);
const isAndroid = () => /Android/i.test(navigator.userAgent);
const isMobileUA = () => isIOS() || isAndroid();
const isInAppWebView = () => /NicelinksApp/i.test(navigator.userAgent) || window.ReactNativeWebView;
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;

const shouldShowAppPromoLogin = () =>
  isMobileUA() && !isInAppWebView() && !isStandalone() && !localStorage.getItem(DISMISS_KEY_LOGIN);

// state
const [showAppPromo, setShowAppPromo] = useState(false);
useEffect(() => { setShowAppPromo(shouldShowAppPromoLogin()); }, []);
const dismissAppPromo = () => { localStorage.setItem(DISMISS_KEY_LOGIN, "1"); setShowAppPromo(false); };


  const onChange = (e) =>
    dispatch({ type: INPUT_EDIT_LOG, inputName: e.target.name, payload: e.target.value });

  const onEmailLogin = async (e) => {
    e.preventDefault();
    const { email, password } = state.credentialsLog;
    if (!email || !password) {
      dispatch({ type: ERROR_CATCHED, payload: t("Login2") });
      return;
    }
    dispatch({ type: START_FETCH });
    try {
      await loginWithEmail(email.trim(), password);
      dispatch({ type: FETCH_SUCCESS });
      newSnack("success", t("Login34"));
      history.push(nextPath || "/home");
    } catch (err) {
      dispatch({ type: ERROR_CATCHED, payload: err?.data?.detail || err.message });
      newSnack("error", t("Login2"));
    }
  };

  const handleLineLogin = () => {
    if (!LINE_CHANNEL_ID) return newSnack("error", "LINE_CHANNEL_ID is not set");
    const stateVal = Math.random().toString(36).slice(2);
    const nonceVal = Math.random().toString(36).slice(2);
    localStorage.setItem("oauth_state", stateVal);
    localStorage.setItem("oauth_nonce", nonceVal);
    localStorage.setItem("oauth_provider", "line");
    const u = new URL("https://access.line.me/oauth2/v2.1/authorize");
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", LINE_CHANNEL_ID);
    u.searchParams.set("redirect_uri", SOCIAL_REDIRECT_URI);
    u.searchParams.set("state", stateVal);
    u.searchParams.set("scope", "profile openid email");
    u.searchParams.set("nonce", nonceVal);
    u.searchParams.set("prompt", "consent");
    window.location.href = u.toString();
  };

    return (
      <Fragment>
        <style>{`
  .nl-app-login {
    border: 0; border-radius: 16px;
    background: radial-gradient(120% 120% at 0% 0%, #dbeafe 0%, #93c5fd 45%, #3b82f6 100%);
    color: #0b1220;
    box-shadow: 0 10px 30px rgba(17, 24, 39, .18);
    overflow: hidden;
  }
  .nl-app-login__body { display:flex; gap:14px; align-items:center; flex-wrap:wrap; }
  .nl-app-login__icon {
    width:48px; height:48px; border-radius:12px; background:#fff;
    display:inline-flex; align-items:center; justify-content:center;
    box-shadow: 0 8px 24px rgba(2,6,23,.12);
  }
  .nl-app-login__title { font-weight:800; font-size:16px; letter-spacing:.01em; }
  .nl-app-login__lead { font-size:12px; opacity:.9 }
  .nl-app-login__badge {
    height:36px; display:inline-block;
    filter: drop-shadow(0 6px 16px rgba(0,0,0,.18));
  }
  .nl-app-login__close {
    position:absolute; right:10px; top:10px; width:28px; height:28px; border:0;
    border-radius:9999px; background:rgba(255,255,255,.8);
    display:inline-flex; align-items:center; justify-content:center;
  }
  @media (max-width:640px){
    .nl-app-login__badge { width:100%; height:auto; }
  }
`}</style>

        <div
          className="nl-auth-hero nl-auth-hero--login"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: `${16 + (topOffset || 0)}px 16px 24px`, // ← ここで反映
            position: "relative",
          }}
        >
          {/* 背景 */}
          <div className="nl-auth-hero__bg" aria-hidden="true" />
        {/* 背景レイヤー */}
        <div className="nl-auth-hero__bg" aria-hidden="true" />

        {/* 左上ロゴ */}
        <div className="nl-auth-logo">
          <Link to="/home" className="d-flex align-items-center text-decoration-none">
            <img src="/assets/images/nicelinks_icon.png" alt="Nicelinks" />
            <span className="fredoka-font ls-3 fw-600 text-current font-xl ms-1">{t("Login4")}</span>
          </Link>
        </div>

        {/* フォーム本体 */}
        <div className="nl-pane">
          <h2 className="nl-pane__title text-current" style={{ fontSize: 36 }}>
            {t("Login6")}
          </h2>

          <form onSubmit={onEmailLogin} className="p-0">
            <div className="form-group mb-2 nl-input">
              <i className="ti-email" aria-hidden="true" />
              <input
                name="email" type="email" value={state.credentialsLog.email} onChange={onChange}
                className="style2-input form-control text-grey-900 font-xsss fw-600"
                placeholder={t("Login14")} autoComplete="email" required
              />
            </div>
            <div className="form-group mb-3 nl-input">
              <i className="ti-lock" aria-hidden="true" />
              <input
                name="password" type={showPw ? "text" : "password"}
                value={state.credentialsLog.password} onChange={onChange}
                className="style2-input form-control text-grey-900 font-xss ls-3"
                placeholder={t("Login15")} autoComplete="current-password" required minLength={6}
              />
              <button type="button" className="btn btn-link p-0 font-xssss nl-toggle"
                onClick={() => setShowPw(v => !v)} aria-label={showPw ? t("Login32") : t("Login33")}>
                {showPw ? t("Login32") : t("Login33")}
              </button>
            </div>

            <button type="submit" className="form-control text-center style2-input text-white fw-600 bg-dark border-0 p-0 nl-cta"
              disabled={state.isLoading}>
              {t("Login13")}
            </button>
            <h6 className="text-grey-600 font-xssss fw-500 mt-2 mb-0 lh-32">
              {t("Login20")} <Link to="/register" className="fw-700 ms-1">{t("Login18")}</Link>
            </h6>
          </form>

          <div className="text-center mt-3">
            <h6 className="mb-0 d-inline-block bg-white fw-500 font-xsss text-grey-600 mb-3">{t("Login23")}</h6>

            <button
              className="form-control text-center style2-input text-white fw-600 shadow-md mt-2 mb-2 p-0 border-0"
              type="button" onClick={handleLineLogin}
              style={{ backgroundColor: "#06C755", borderRadius: 9999, padding: 10, position: "relative",
                       display: "flex", justifyContent: "center", alignItems: "center" }}
              disabled={state.isLoading}
            >
              <img src="/assets/images/login_line.png" alt="LINE" style={{ position: "absolute", left: 15, height: 35 }} />
              <span>{t("Login24")}</span>
            </button>

            <button
              className="form-control text-center style2-input text-white fw-600 shadow-md mt-2 mb-2 p-0 border-0"
              type="button"
              onClick={() => {
                const stateVal = Math.random().toString(36).slice(2);
                const nonceVal = Math.random().toString(36).slice(2);
                localStorage.setItem("oauth_state", stateVal);
                localStorage.setItem("oauth_nonce", nonceVal);
                localStorage.setItem("oauth_provider", "google");
                const u = new URL("https://accounts.google.com/o/oauth2/v2/auth");
                u.searchParams.set("response_type", "code");
                u.searchParams.set("client_id", process.env.REACT_APP_CLIENTID);
                u.searchParams.set("redirect_uri", SOCIAL_REDIRECT_URI);
                u.searchParams.set("state", stateVal);
                u.searchParams.set("scope", "openid email profile");
                u.searchParams.set("nonce", nonceVal);
                u.searchParams.set("prompt", "consent");
                window.location.href = u.toString();
              }}
              style={{ backgroundColor: "#4285F4", borderRadius: 9999, padding: 10, position: "relative",
                       display: "flex", justifyContent: "center", alignItems: "center" }}
              disabled={state.isLoading}
            >
              <img src="/assets/images/login_google.png" alt="Google" style={{ position: "absolute", left: 15, height: 35 }} />
              <span>{t("Login26")}</span>
            </button>
          </div>


          {state.isLoading && <div className="d-flex justify-content-center mt-3"><CircularProgress size={28} /></div>}
          {state.error && <p className="text-danger mt-2">{state.error}</p>}

          <div className="col-md-12 mt-2">
            <a href="https://tatami.tokyo/pricing/" className="me-3">
              <span className="font-xssss text-grey-600">{t("Login30")}</span>
            </a>
            <a href="https://tatami.tokyo/terms-conditions-ad/">
              <span className="font-xssss text-grey-600 text-decoration-underline pointer">{t("Login31")}</span>
            </a>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default withCookies(Login);
